import React, { useState, useEffect } from "react";
import Head from "next/head";
import dynamic from "next/dynamic";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useRouter } from "next/router";
import Header from "../Header";
import Footer from "../Footer";
import { LOGIN } from "../../routes/RouteConstants";
import { getPreloadFontsLinks } from "../../services/utils";
import UserService from "../../services/UserService";
import { getMetaDataVersion } from "../../containers/JobPosting/jobPostingActions";
import useCreditProfileStore from "../../zustandStores/userCreditProfileStore";
import useMetadataStore from "../../zustandStores/metadataStore";
import PremiumPopupRenderer from "../PremiumPopupRenderer";
import SubHeader from "../SubHeader";
import HeaderV2 from "tailwindLocalComponents/Headerv2";
import useNavStore, { useInitializeNav } from "zustandStores/useNavStore";
import {
  getHeaderConfig,
  getSidebarConfig,
  layoutConfig
} from "utils/layoutConfig";
import classes from "./layout.module.css";
import { useIsSmallScreen } from "hooks/useDeviceType";
import useAuthLoader from "../../hooks/useAuthLoader";
import Loader from "../Loader";

const CompanyNameBanner = dynamic(
  () => import("components/CompanyNameBanner"),
  {
    ssr: false
  }
);
const Sidebar = dynamic(() => import("components/SideBar/Sidebarv2"), {
  ssr: false
});
import { WhatsAppBroadcastPopup } from "components/WhatsAppBroadcastPopup";
import { ShareJobModal } from "components/JobListv2";

const VideoPlayerInBackdrop = dynamic(() =>
  import("components/VideoPlayer/VideoPlayerInBackdrop")
);

const DocumentViewer = dynamic(() => import("components/DocumentViewer"), {
  ssr: false
});

const BlackListedRecruiter = dynamic(() => import("../BlackListedRecruiter"), {
  ssr: false
});
const CreditConsumption = dynamic(() => import("../CreditConsumptionModal"), {
  ssr: false
});
const ApiLoader = dynamic(() => import("../ApiLoader"), { ssr: false });
const JobBoostModal = dynamic(() => import("../BoostModal"), {
  ssr: false
});
const ContactSalesModal = dynamic(() => import("../ContactSalesModal"), {
  ssr: false
});
const DatabaseJobsModal = dynamic(() => import("../DatabaseJobsModal"), {
  ssr: false
});
const BulkWhatsAppJobsModal = dynamic(
  () => import("../BulkWhatsAppJobsModal"),
  {
    ssr: false
  }
);
const VideoModal = dynamic(() => import("../VideoModal"), {
  ssr: false
});

const TeamUserAdditionModal = dynamic(
  () => import("components/TeamUserAdditionModal"),
  {
    ssr: false
  }
);

const UnlockNumberModal = dynamic(
  () => import("components/UnlockNumberModal"),
  {
    ssr: false
  }
);

const KnowWhatsNewModal = dynamic(
  () => import("components/KnowWhatsNewModal"),
  {
    ssr: false
  }
);

const Layout = ({
  children,
  isFooter = true,
  isLogin = false,
  isPostNewJob = true,
  isHome = false,
  onlyLogo = false,
  isHeaderFixed = true,
  pathname,
  isNoIndexFollow = false,
  breadcrumbList = [],
  title = "",
  metaDescription = "",
  canonical = "",
  isClick = true,
  logoutOnly = false,
  metaOgImage,
  contentClass = null,
  showHeader = true,
  isNativeApp = "",
  headerMargin = false,
  subHeader = false,
  pageTitle = "",
  isBack = false,
  onBackClick,
  getMetaDataVersion,
  isAuthNavigationEnabled = true
}) => {
  const router = useRouter();
  const isSmallScreen = useIsSmallScreen();
  const { navState } = useNavStore(state => ({
    navState: state.navState
  }));
  const { isLoading: isAuthLoading } = useAuthLoader({
    enabled: isAuthNavigationEnabled
  });

  useInitializeNav(isSmallScreen);

  useEffect(() => {
    UserService.commonCalls({
      getMetaDataVersion,
      getUserCreditProfile: useCreditProfileStore.getState().fetchData,
      getJobPricingMetaData: useMetadataStore.getState().fetchMetadata
    });
  }, []);

  const sidebarConfig = getSidebarConfig(router.pathname);
  const config = getHeaderConfig(router.pathname);

  const { useV2Layout, v2Root } = sidebarConfig;
  const currentLayoutConfig = layoutConfig[router.pathname] || {};
  const showTeamAddUserModal =
    currentLayoutConfig?.showTeamAddUserModal || false;
  const containerStyle = currentLayoutConfig?.containerStyle || {
    useContainer: false
  };
  const backgroundColor = currentLayoutConfig?.backgroundColor || "";
  const containerClasses = containerStyle.useContainer
    ? "max-w-[1280px] w-full p-3 md:p-6 mx-auto"
    : "";

  let breadcrumbSchema = [];
  if (breadcrumbList?.length) {
    breadcrumbSchema = breadcrumbList.map((item, index) => {
      if (index === 0) {
        return {
          "@type": "ListItem",
          position: index + 1,
          item: {
            "@id": `${item.url}`,
            name: `${item.label}`
          }
        };
      }
      if (index === breadcrumbList.length - 1) {
        return {
          "@type": "ListItem",
          position: index + 1,
          item: {
            name: `${item.label}`
          }
        };
      }
      return {
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@id": `${item.url}`,
          name: `${item.label}`
        }
      };
    });
  }

  const breadcrumbPrint = {
    "@context": "http://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbSchema
  };
  const getMarginClass = () => {
    if ((!sidebarConfig.visible && !config.visibleWithBackBtn) || isSmallScreen)
      return "";
    return navState ? "ml-64" : "ml-16";
  };
  return (
    <>
      <Head>
        {getPreloadFontsLinks()}
        {isNoIndexFollow ? (
          <meta name="robots" content="noindex, follow" />
        ) : null}
        {canonical ? <link rel="canonical" href={canonical} /> : null}
        {title ? <title>{title}</title> : null}
        {metaDescription ? (
          <meta name="description" content={metaDescription} />
        ) : null}
        {canonical ? <meta property="og:url" content={canonical} /> : null}
        {title ? <meta property="og:title" content={title} /> : null}
        {metaDescription ? (
          <meta property="og:description" content={metaDescription} />
        ) : null}
        <meta property="og:type" content="website" />
        <meta property="og:image" content={metaOgImage} />
        {breadcrumbList ? (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(breadcrumbPrint)
            }}
          />
        ) : null}
      </Head>
      {isAuthLoading ? (
        <Loader />
      ) : useV2Layout ? (
        <div className={v2Root ? v2Root : "flex min-h-screen"}>
          {(sidebarConfig.visible || config.visibleWithBackBtn) && <Sidebar />}
          <div
            className={`flex-1 flex flex-col transition-all duration-300 ${getMarginClass()} ${backgroundColor}`}
          >
            {showHeader && !isNativeApp ? <HeaderV2 /> : null}
            <main className={`flex-1 ${classes.main} ${containerClasses}`}>
              {children}
            </main>
          </div>
        </div>
      ) : (
        <div className={isHeaderFixed ? classes.root : classes.rootNonFixed}>
          {showHeader && !isNativeApp ? (
            <Header
              isPostNewJob={isPostNewJob}
              pathname={pathname}
              onlyLogo={onlyLogo}
              isHeaderFixed={isHeaderFixed}
              isLogin={isLogin}
              isClick={isClick}
              logoutOnly={logoutOnly}
              headerMargin={headerMargin}
            />
          ) : null}
          {subHeader ? (
            <SubHeader
              title={pageTitle || title}
              isBack={isBack}
              onBackClick={onBackClick}
            />
          ) : null}
          <div
            className={
              contentClass
                ? contentClass
                : isLogin || pathname === LOGIN
                ? isFooter
                  ? classes.loginContent
                  : null
                : !isHome
                ? classes.content
                : null
            }
          >
            {children}
          </div>
          {isFooter && <Footer isLogin={isLogin} pathname={pathname} />}
        </div>
      )}
      <ApiLoader />
      <WhatsAppBroadcastPopup />
      <JobBoostModal />
      <BlackListedRecruiter />
      <CreditConsumption />
      <PremiumPopupRenderer />
      <CompanyNameBanner />
      <ContactSalesModal />
      <DatabaseJobsModal />
      <BulkWhatsAppJobsModal />
      <VideoModal />
      <VideoPlayerInBackdrop />
      <DocumentViewer />
      <UnlockNumberModal />
      <KnowWhatsNewModal />
      <ShareJobModal />

      {showTeamAddUserModal && <TeamUserAdditionModal />}
    </>
  );
};

Layout.propTypes = {
  pathname: PropTypes.string,
  onlyLogo: PropTypes.bool,
  isFooter: PropTypes.bool,
  isLogin: PropTypes.bool,
  title: PropTypes.any,
  metaDescription: PropTypes.any,
  canonical: PropTypes.any,
  isNoIndexFollow: PropTypes.bool,
  breadcrumbList: PropTypes.any,
  contentClass: PropTypes.object
};

const mapStateToProps = state => ({
  isNativeApp: state.commonReducer.isNativeApp
});

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      getMetaDataVersion
    },
    dispatch
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Layout);
